export function initThemeToggle(): void {
  const themeToggle = document.getElementById('theme-toggle') as HTMLButtonElement | null;
  if (!themeToggle) return;

  // Get initial theme from localStorage or system preference
  const getInitialTheme = (): 'light' | 'dark' => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      return savedTheme;
    }

    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  // Apply theme
  const applyTheme = (theme: 'light' | 'dark'): void => {
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('theme', 'light');
    }

    // Update icon (assuming we have sun/moon icons)
    updateThemeIcon(theme);

    // Update favicon and apple-touch-icon
    updateFavicons(theme);

    // Update theme-color meta
    updateThemeColor(theme);
  };

  // Update favicon and apple-touch-icon based on theme
  const updateFavicons = (theme: 'light' | 'dark'): void => {
    const faviconLink = document.getElementById('favicon-link') as HTMLLinkElement | null;
    const appleTouchIconLink = document.getElementById(
      'apple-touch-icon-link'
    ) as HTMLLinkElement | null;

    if (faviconLink) {
      faviconLink.href = theme === 'dark' ? '/favicon-dark.svg' : '/favicon.svg';
    }

    if (appleTouchIconLink) {
      appleTouchIconLink.href =
        theme === 'dark'
          ? '/src/assets/img/apple-touch-icon/dark.svg'
          : '/src/assets/img/apple-touch-icon/light.svg';
    }
  };

  // Update theme-color meta tag
  const updateThemeColor = (theme: 'light' | 'dark'): void => {
    // Remove existing theme-color meta tags
    const existingMetaTags = document.querySelectorAll('meta[name="theme-color"]');
    existingMetaTags.forEach((tag) => tag.remove());

    // Create new theme-color meta tag
    const metaTag = document.createElement('meta');
    metaTag.name = 'theme-color';
    metaTag.content = theme === 'dark' ? '#1a1a1a' : '#ffffff';

    // Insert into head
    const head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(metaTag);
  };

  // Update icon based on theme
  const updateThemeIcon = (theme: 'light' | 'dark'): void => {
    const icon = themeToggle.querySelector('svg use');
    if (icon) {
      // Get the current href from the icon to extract the sprite path
      let currentHref = icon.getAttribute('href') || '';

      // If href is empty, try to find sprite path from other use elements on the page
      if (!currentHref) {
        const otherUse = document.querySelector('svg use[href*="sprite"]');
        if (otherUse) {
          currentHref = otherUse.getAttribute('href') || '';
        }
      }

      // Extract sprite path (everything before #)
      const spritePath = currentHref.split('#')[0] || '/assets/img/sprite.svg';
      // Use the sprite path with the appropriate icon
      icon.setAttribute(
        'href',
        theme === 'dark' ? `${spritePath}#icon-moon` : `${spritePath}#icon-sun`
      );
    }
  };

  // Initialize theme
  const initialTheme = getInitialTheme();
  applyTheme(initialTheme);

  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (!localStorage.getItem('theme')) {
      applyTheme(e.matches ? 'dark' : 'light');
    }
  });

  // Toggle theme on button click
  themeToggle.addEventListener('click', () => {
    const currentTheme =
      document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    applyTheme(newTheme);
  });
}
